import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { fmtCurrency } from '../lib/localeUtils';
import { exportCSV, exportPDF } from '../lib/reportExportUtils';
import {
  ClipboardCheck, Plus, Loader2, Search, ChevronLeft, ChevronRight,
  CheckCircle2, XCircle, Clock, AlertTriangle, ArrowUpDown, Send,
  ShieldCheck, Ban, TrendingDown, TrendingUp, Package, Download,
  FileDown, Printer, ChevronDown, ChevronUp, MapPin
} from 'lucide-react';
import { keyed } from '../lib/listKeys';

const STATUS_MAP = {
  in_progress: { label: 'In Progress', color: 'bg-blue-500/10 text-blue-600 border-blue-500/30', icon: Clock },
  pending_approval: { label: 'Pending Approval', color: 'bg-orange-500/10 text-orange-600 border-orange-500/30', icon: AlertTriangle },
  approved: { label: 'Approved', color: 'bg-green-500/10 text-green-600 border-green-500/30', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: 'bg-red-500/10 text-red-600 border-red-500/30', icon: XCircle },
};

export default function StockTakePage() {
  const [sessions, setSessions] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableBins, setAvailableBins] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedBins, setSelectedBins] = useState([]);
  const [createNotes, setCreateNotes] = useState('');

  // Active session state
  const [activeSession, setActiveSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [countSearch, setCountSearch] = useState('');
  const [localCounts, setLocalCounts] = useState({});
  const [batchCounts, setBatchCounts] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedBatches, setExpandedBatches] = useState({});

  // Variance
  const [variance, setVariance] = useState(null);
  const [varianceLoading, setVarianceLoading] = useState(false);

  // Approve dialog
  const [approveDialog, setApproveDialog] = useState(null);
  const [approving, setApproving] = useState(false);

  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem('pharmxai_user') || '{}');
      setUserRole(u.role || '');
    } catch { /* */ }
  }, []);

  const isAdmin = userRole === 'super_admin' || userRole === 'admin';

  const fetchSessions = useCallback(async () => {
    try {
      const res = await api.get('/stocktake/sessions', { params: { page, limit: 15 } });
      setSessions(res.data.sessions || []);
      setPages(res.data.pages || 1);
    } catch { /* */ } finally { setLoading(false); }
  }, [page]);

  const fetchActiveSessions = useCallback(async () => {
    try {
      const res = await api.get('/stocktake/sessions/active');
      setActiveSessions(res.data.sessions || []);
    } catch { /* */ }
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);
  useEffect(() => { fetchActiveSessions(); }, [fetchActiveSessions]);

  const openCreateDialog = async () => {
    setCreateOpen(true);
    setSelectedCategories([]);
    setSelectedBins([]);
    setCreateNotes('');
    try {
      const [catRes, binRes] = await Promise.all([
        api.get('/stocktake/categories'),
        api.get('/stocktake/bins')
      ]);
      setAvailableCategories(catRes.data.categories || []);
      setAvailableBins(binRes.data.bins || []);
    } catch { /* */ }
  };

  const createSession = async () => {
    setCreating(true);
    try {
      const body = { notes: createNotes };
      if (selectedCategories.length > 0) body.categories = selectedCategories;
      if (selectedBins.length > 0) body.bin_locations = selectedBins;
      const res = await api.post('/stocktake/sessions', body);
      toast.success(`Stock take started with ${res.data.total_products} products`);
      setCreateOpen(false);
      setActiveSession(res.data);
      initLocalCounts(res.data.items);
      fetchSessions();
      fetchActiveSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create session');
    } finally { setCreating(false); }
  };

  const openSession = async (sessionId) => {
    setSessionLoading(true);
    try {
      const res = await api.get(`/stocktake/sessions/${sessionId}`);
      setActiveSession(res.data);
      initLocalCounts(res.data.items);
      if (res.data.status !== 'in_progress') loadVariance(sessionId);
    } catch { toast.error('Failed to load session'); }
    finally { setSessionLoading(false); }
  };

  const initLocalCounts = (items) => {
    const counts = {};
    const bc = {};
    items.forEach(it => {
      if (it.counted_quantity !== null && it.counted_quantity !== undefined) {
        counts[it.product_id] = String(it.counted_quantity);
      }
      (it.batches || []).forEach(b => {
        if (b.counted_quantity !== null && b.counted_quantity !== undefined) {
          bc[`${it.product_id}:${b.batch_number}`] = String(b.counted_quantity);
        }
      });
    });
    setLocalCounts(counts);
    setBatchCounts(bc);
  };

  const saveCounts = async () => {
    if (!activeSession) return;
    setSaving(true);
    try {
      const entries = [];
      for (const [pid, v] of Object.entries(localCounts)) {
        if (v === '' || v === undefined) continue;
        const item = activeSession.items.find(i => i.product_id === pid);
        const batchEntries = (item?.batches || [])
          .map(b => {
            const bv = batchCounts[`${pid}:${b.batch_number}`];
            if (bv !== undefined && bv !== '') return { batch_number: b.batch_number, counted_quantity: parseInt(bv, 10) || 0 };
            return null;
          })
          .filter(Boolean);
        entries.push({
          product_id: pid,
          counted_quantity: parseInt(v, 10) || 0,
          batch_entries: batchEntries.length > 0 ? batchEntries : undefined,
        });
      }
      if (entries.length === 0) { toast.error('No counts entered'); setSaving(false); return; }
      const res = await api.post(`/stocktake/sessions/${activeSession.session_id}/entry`, { items: entries });
      toast.success(res.data.message);
      const updated = await api.get(`/stocktake/sessions/${activeSession.session_id}`);
      setActiveSession(updated.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const submitForApproval = async () => {
    if (!activeSession) return;
    setSubmitting(true);
    try {
      await api.post(`/stocktake/sessions/${activeSession.session_id}/submit`);
      toast.success('Submitted for admin approval');
      const updated = await api.get(`/stocktake/sessions/${activeSession.session_id}`);
      setActiveSession(updated.data);
      loadVariance(activeSession.session_id);
      fetchSessions();
      fetchActiveSessions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Submit failed'); }
    finally { setSubmitting(false); }
  };

  const loadVariance = async (sid) => {
    setVarianceLoading(true);
    try {
      const res = await api.get(`/stocktake/sessions/${sid}/variance`);
      setVariance(res.data);
    } catch { toast.error('Failed to load variance'); }
    finally { setVarianceLoading(false); }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      const res = await api.post(`/stocktake/sessions/${approveDialog}/approve`);
      toast.success(res.data.message);
      setApproveDialog(null);
      if (activeSession?.session_id === approveDialog) {
        const updated = await api.get(`/stocktake/sessions/${approveDialog}`);
        setActiveSession(updated.data);
      }
      fetchSessions();
      fetchActiveSessions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Approval failed'); }
    finally { setApproving(false); }
  };

  const handleCancel = async (sid) => {
    try {
      await api.post(`/stocktake/sessions/${sid}/cancel`);
      toast.success('Session cancelled');
      if (activeSession?.session_id === sid) setActiveSession(null);
      fetchSessions();
      fetchActiveSessions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Cancel failed'); }
  };

  const handlePrint = () => {
    if (!activeSession) return;
    const brand = JSON.parse(localStorage.getItem('pharmxai_settings') || '{}');
    const bName = brand.business_name || 'DutiLyne';
    const bTag = brand.tagline || 'AI-Powered Pharmacy Management';
    const bColor = brand.primary_color || '#14b8a6';
    const bLogo = brand.logo_url || '';
    const bAddr = brand.business_address || '';
    const bPhone = brand.contact_phone || '';
    const bEmail = brand.contact_email || '';

    const rows = activeSession.items.map((it, i) => `
      <tr${i % 2 === 1 ? ' style="background:#f9fafb;"' : ''}>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;font-size:11px;">${i + 1}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;font-size:11px;font-weight:500;">${it.product_name}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;font-size:11px;">${it.category || ''}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;font-size:11px;">${it.bin_location || '-'}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;font-size:11px;">${(it.batches || []).map(b => b.batch_number).join(', ') || '-'}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;font-size:11px;width:80px;border:1px solid #ccc;"></td>
      </tr>
    `).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Stock Take Sheet — ${bName}</title>
<style>
  @page { size: portrait; margin: 20mm; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .no-print { display: none; } }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', -apple-system, sans-serif; font-size: 13px; color: #333; padding: 20px; }
</style></head><body>
  <button class="no-print" onclick="window.print()" style="background:${bColor};color:#fff;padding:10px 24px;border:none;border-radius:6px;font-size:14px;cursor:pointer;margin-bottom:20px;">Print</button>
  <div style="max-width:900px;margin:0 auto;">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid ${bColor};padding-bottom:20px;margin-bottom:24px;">
      <div>
        ${bLogo ? `<img src="${bLogo}" alt="Logo" style="max-height:50px;margin-bottom:8px;display:block;">` : ''}
        <h1 style="margin:0;font-size:28px;color:${bColor};">${bName}</h1>
        <p style="margin:4px 0 0;color:#666;font-size:12px;">${bTag}</p>
        ${bAddr ? `<p style="margin:2px 0 0;color:#888;font-size:11px;">${bAddr}</p>` : ''}
        ${(bPhone || bEmail) ? `<p style="margin:2px 0 0;color:#888;font-size:11px;">${[bPhone, bEmail].filter(Boolean).join(' | ')}</p>` : ''}
      </div>
      <div style="text-align:right;">
        <h2 style="margin:0;font-size:20px;color:#333;text-transform:uppercase;letter-spacing:2px;">Stock Take</h2>
        <p style="margin:4px 0;font-size:11px;color:#666;font-family:monospace;">${activeSession.session_id}</p>
        <p style="margin:4px 0;font-size:11px;color:#666;">Date: ${new Date(activeSession.created_at).toLocaleDateString()}</p>
        <p style="margin:4px 0;font-size:11px;color:#666;">${activeSession.total_products} Products</p>
      </div>
    </div>
    <table style="width:100%;border-collapse:collapse;margin-bottom:20px;">
      <thead><tr style="background:${bColor};">
        <th style="color:#fff;padding:10px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;">#</th>
        <th style="color:#fff;padding:10px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;">Product</th>
        <th style="color:#fff;padding:10px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;">Category</th>
        <th style="color:#fff;padding:10px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;">Bin</th>
        <th style="color:#fff;padding:10px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;">Batches</th>
        <th style="color:#fff;padding:10px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:0.5px;">Count</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div style="text-align:center;padding-top:24px;border-top:2px solid ${bColor};margin-top:30px;color:#666;font-size:11px;">
      <p>${bName} — Confidential</p>
      <p style="font-size:10px;color:#999;margin-top:4px;">Printed: ${new Date().toLocaleString()}</p>
    </div>
  </div>
</body></html>`;

    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) { toast.error('Please allow popups'); return; }
    w.document.write(html);
    w.document.close();
    w.focus();
  };

  const handleExportCSV = () => {
    if (!activeSession) return;
    const rows = activeSession.items.map(it => ({
      product_name: it.product_name,
      category: it.category || '',
      bin_location: it.bin_location || '',
      batches: (it.batches || []).map(b => `${b.batch_number}(${b.system_quantity ?? ''})`).join('; '),
      ...(isAdmin ? { system_quantity: it.system_quantity ?? '' } : {}),
      counted_quantity: it.counted_quantity ?? '',
    }));
    exportCSV(rows, `StockTake_${activeSession.session_id}`);
    toast.success('CSV exported');
  };

  const handleExportPDF = () => {
    if (!activeSession) return;
    const rows = activeSession.items.map(it => ({
      product_name: it.product_name,
      category: it.category || '',
      bin: it.bin_location || '',
      batches: (it.batches || []).map(b => b.batch_number).join(', '),
      ...(isAdmin ? { system_qty: it.system_quantity ?? '' } : {}),
      counted: it.counted_quantity ?? '',
    }));
    exportPDF(rows, `StockTake_${activeSession.session_id}`, {
      session: activeSession.session_id,
      date: new Date(activeSession.created_at).toLocaleDateString(),
      products: activeSession.total_products,
    });
    toast.success('PDF exported');
  };

  const toggleBatchExpand = (pid) => setExpandedBatches(prev => ({ ...prev, [pid]: !prev[pid] }));

  const filteredItems = activeSession?.items?.filter(it =>
    !countSearch || it.product_name.toLowerCase().includes(countSearch.toLowerCase()) ||
    it.product_id.toLowerCase().includes(countSearch.toLowerCase()) ||
    (it.category || '').toLowerCase().includes(countSearch.toLowerCase()) ||
    (it.bin_location || '').toLowerCase().includes(countSearch.toLowerCase())
  ) || [];

  const countedCount = activeSession?.items?.filter(it => localCounts[it.product_id] !== undefined && localCounts[it.product_id] !== '').length || 0;

  // ── Session Detail View ──
  if (activeSession) {
    const s = activeSession;
    const isEditable = s.status === 'in_progress';

    return (
      <div className="space-y-4" data-testid="stocktake-session-view">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <Button variant="ghost" size="sm" onClick={() => { setActiveSession(null); setVariance(null); }} className="gap-1 mb-1 -ml-2">
              <ChevronLeft className="h-4 w-4" /> Back
            </Button>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-primary" /> {s.session_id}
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              {new Date(s.created_at).toLocaleString()} by {s.created_by} | {s.total_products} products
              {s.filters?.categories?.length > 0 && ` | Categories: ${s.filters.categories.join(', ')}`}
              {s.filters?.bin_locations?.length > 0 && ` | Bins: ${s.filters.bin_locations.join(', ')}`}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {(() => { const st = STATUS_MAP[s.status]; const Icon = st?.icon || Clock; return <Badge variant="outline" className={`gap-1 ${st?.color || ''}`}><Icon className="h-3 w-3" />{st?.label}</Badge>; })()}
            <Button size="sm" variant="outline" onClick={handlePrint} className="gap-1.5 text-xs" data-testid="print-btn"><Printer className="h-3.5 w-3.5" /> Print</Button>
            <Button size="sm" variant="outline" onClick={handleExportCSV} className="gap-1.5 text-xs" data-testid="export-csv-btn"><Download className="h-3.5 w-3.5" /> CSV</Button>
            <Button size="sm" variant="outline" onClick={handleExportPDF} className="gap-1.5 text-xs" data-testid="export-pdf-btn"><FileDown className="h-3.5 w-3.5" /> PDF</Button>
            {isEditable && (
              <>
                <Button size="sm" variant="outline" onClick={saveCounts} disabled={saving} className="gap-1.5" data-testid="save-counts-btn">
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ClipboardCheck className="h-3.5 w-3.5" />} Save
                </Button>
                <Button size="sm" onClick={submitForApproval} disabled={submitting || countedCount === 0} className="gap-1.5" data-testid="submit-approval-btn">
                  {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Submit
                </Button>
              </>
            )}
            {s.status === 'pending_approval' && isAdmin && (
              <Button size="sm" onClick={() => setApproveDialog(s.session_id)} className="gap-1.5 bg-green-600 hover:bg-green-700" data-testid="approve-btn">
                <ShieldCheck className="h-3.5 w-3.5" /> Approve
              </Button>
            )}
            {(s.status === 'in_progress' || s.status === 'pending_approval') && (
              <Button size="sm" variant="destructive" onClick={() => handleCancel(s.session_id)} className="gap-1.5" data-testid="cancel-session-btn">
                <Ban className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>

        {/* Progress */}
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-4 text-sm">
              <span>Counted: <strong>{countedCount}</strong> / {s.total_products}</span>
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${Math.round(countedCount / Math.max(s.total_products, 1) * 100)}%` }} />
              </div>
              <span className="text-muted-foreground">{Math.round(countedCount / Math.max(s.total_products, 1) * 100)}%</span>
            </div>
          </CardContent>
        </Card>

        {/* Variance Report */}
        {variance && (
          <Card className="border-orange-500/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <ArrowUpDown className="h-4 w-4 text-orange-500" /> Variance Report
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
                {keyed([
                  { label: 'Counted', value: variance.summary.counted },
                  { label: 'Not Counted', value: variance.summary.not_counted },
                  { label: 'Match', value: variance.summary.matches, color: 'text-green-500' },
                  { label: 'Over', value: variance.summary.over, color: 'text-blue-500' },
                  { label: 'Under', value: variance.summary.under, color: 'text-red-500' },
                  { label: 'Cost Var.', value: fmtCurrency(variance.summary.total_cost_variance), color: variance.summary.total_cost_variance < 0 ? 'text-red-500' : 'text-green-500' },
                  { label: 'Sell Var.', value: fmtCurrency(variance.summary.total_selling_variance), color: variance.summary.total_selling_variance < 0 ? 'text-red-500' : 'text-green-500' },
                ], card => card.label).map(({ item: c, key }) => (
                  <div key={key} className="p-2 bg-muted/50 rounded-lg text-center">
                    <p className="text-[10px] text-muted-foreground">{c.label}</p>
                    <p className={`text-sm font-bold ${c.color || ''}`}>{c.value}</p>
                  </div>
                ))}
              </div>
              {variance.variances.filter(v => v.variance !== 0).length > 0 && (
                <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead>Bin</TableHead>
                        <TableHead className="text-center">System</TableHead>
                        <TableHead className="text-center">Counted</TableHead>
                        <TableHead className="text-center">Variance</TableHead>
                        <TableHead className="text-right">Cost Impact</TableHead>
                        <TableHead className="text-right">Sell Impact</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {keyed(variance.variances.filter(v => v.variance !== 0), v => v.product_id || v.product_name).map(({ item: v, key }) => (
                        <TableRow key={key} className={v.variance < 0 ? 'bg-red-500/5' : 'bg-blue-500/5'}>
                          <TableCell>
                            <span className="text-sm font-medium">{v.product_name}</span>
                            <span className="text-[10px] text-muted-foreground ml-1">{v.category}</span>
                            {v.batch_variances?.length > 0 && (
                              <div className="mt-1 space-y-0.5">
                                {keyed(v.batch_variances, bv => bv.batch_number).map(({ item: bv, key }) => (
                                  <div key={key} className="text-[10px] text-muted-foreground flex gap-2">
                                    <span className="font-mono">{bv.batch_number}</span>
                                    <span>sys:{bv.system_quantity} cnt:{bv.counted_quantity}</span>
                                    <span className={bv.variance < 0 ? 'text-red-500' : bv.variance > 0 ? 'text-blue-500' : ''}>{bv.variance > 0 ? '+' : ''}{bv.variance}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">{v.bin_location || '-'}</TableCell>
                          <TableCell className="text-center">{v.system_quantity}</TableCell>
                          <TableCell className="text-center font-semibold">{v.counted_quantity}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className={`text-xs gap-0.5 ${v.variance < 0 ? 'text-red-500 border-red-500/30' : 'text-blue-500 border-blue-500/30'}`}>
                              {v.variance > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                              {v.variance > 0 ? '+' : ''}{v.variance}
                            </Badge>
                          </TableCell>
                          <TableCell className={`text-right text-sm ${v.cost_impact < 0 ? 'text-red-500' : 'text-green-500'}`}>{fmtCurrency(v.cost_impact)}</TableCell>
                          <TableCell className={`text-right text-sm ${v.selling_impact < 0 ? 'text-red-500' : 'text-green-500'}`}>{fmtCurrency(v.selling_impact)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Count Entry Table */}
        {isEditable && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Count Entry</CardTitle>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input placeholder="Search product, category, bin..." value={countSearch} onChange={(e) => setCountSearch(e.target.value)} className="pl-9 h-8 text-sm" data-testid="count-search" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="max-h-[50vh]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead className="hidden sm:table-cell">Bin</TableHead>
                      {isAdmin && <TableHead className="text-center w-20">System</TableHead>}
                      <TableHead className="text-center w-28">Count</TableHead>
                      {isAdmin && <TableHead className="text-center w-16">Diff</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.map(item => {
                      const counted = localCounts[item.product_id];
                      const hasBatches = (item.batches || []).length > 1;
                      const expanded = expandedBatches[item.product_id];
                      const diff = isAdmin && counted !== '' && counted !== undefined && item.system_quantity !== undefined
                        ? parseInt(counted, 10) - item.system_quantity : null;
                      return [
                        <TableRow key={item.product_id} data-testid={`count-row-${item.product_id}`}>
                          <TableCell className="pr-0">
                            {hasBatches && (
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => toggleBatchExpand(item.product_id)} data-testid={`batch-expand-${item.product_id}`}>
                                {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                              </Button>
                            )}
                          </TableCell>
                          <TableCell>
                            <p className="text-sm font-medium">{item.product_name}</p>
                            <p className="text-[10px] text-muted-foreground">{item.category}{hasBatches ? ` | ${item.batches.length} batches` : ''}</p>
                          </TableCell>
                          <TableCell className="hidden sm:table-cell text-xs">{item.bin_location || '-'}</TableCell>
                          {isAdmin && <TableCell className="text-center text-sm">{item.system_quantity ?? '-'}</TableCell>}
                          <TableCell className="text-center">
                            <Input type="number" min={0} value={counted ?? ''} onChange={(e) => setLocalCounts(prev => ({ ...prev, [item.product_id]: e.target.value }))}
                              className="w-24 h-8 text-center mx-auto text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              placeholder="—" data-testid={`count-input-${item.product_id}`} />
                          </TableCell>
                          {isAdmin && (
                            <TableCell className="text-center">
                              {diff !== null && <span className={`text-sm font-medium ${diff < 0 ? 'text-red-500' : diff > 0 ? 'text-blue-500' : 'text-green-500'}`}>{diff > 0 ? '+' : ''}{diff}</span>}
                            </TableCell>
                          )}
                        </TableRow>,
                        // Batch rows (expanded)
                        ...(expanded ? (item.batches || []).map(batch => (
                          <TableRow key={`${item.product_id}-${batch.batch_number}`} className="bg-muted/30">
                            <TableCell></TableCell>
                            <TableCell className="pl-8">
                              <span className="text-xs font-mono">{batch.batch_number}</span>
                              {batch.expiry_date && <span className="text-[10px] text-muted-foreground ml-2">Exp: {batch.expiry_date}</span>}
                            </TableCell>
                            <TableCell className="hidden sm:table-cell"></TableCell>
                            {isAdmin && <TableCell className="text-center text-xs">{batch.system_quantity ?? '-'}</TableCell>}
                            <TableCell className="text-center">
                              <Input type="number" min={0}
                                value={batchCounts[`${item.product_id}:${batch.batch_number}`] ?? ''}
                                onChange={(e) => setBatchCounts(prev => ({ ...prev, [`${item.product_id}:${batch.batch_number}`]: e.target.value }))}
                                className="w-20 h-7 text-center mx-auto text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                placeholder="—" data-testid={`batch-count-${item.product_id}-${batch.batch_number}`} />
                            </TableCell>
                            {isAdmin && (
                              <TableCell className="text-center">
                                {(() => {
                                  const bv = batchCounts[`${item.product_id}:${batch.batch_number}`];
                                  if (bv === '' || bv === undefined || batch.system_quantity === undefined) return null;
                                  const bd = parseInt(bv, 10) - batch.system_quantity;
                                  return <span className={`text-xs ${bd < 0 ? 'text-red-500' : bd > 0 ? 'text-blue-500' : 'text-green-500'}`}>{bd > 0 ? '+' : ''}{bd}</span>;
                                })()}
                              </TableCell>
                            )}
                          </TableRow>
                        )) : [])
                      ];
                    }).flat()}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Approve Dialog */}
        <Dialog open={!!approveDialog} onOpenChange={() => setApproveDialog(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-green-500" /> Approve Stock Take</DialogTitle>
              <DialogDescription>This will update all product quantities. Cannot be undone.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setApproveDialog(null)}>Cancel</Button>
              <Button onClick={handleApprove} disabled={approving} className="bg-green-600 hover:bg-green-700 gap-1.5" data-testid="confirm-approve-btn">
                {approving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Approve
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ── Sessions List ──
  return (
    <div className="space-y-4" data-testid="stocktake-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <ClipboardCheck className="h-6 w-6 text-primary" /> Stock Take
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">Physical inventory count with variance reporting</p>
        </div>
        <Button onClick={openCreateDialog} className="gap-1.5 h-10" data-testid="new-stocktake-btn">
          <Plus className="h-4 w-4" /> New Stock Take
        </Button>
      </div>

      {activeSessions.length > 0 && (
        <div data-testid="active-stocktakes-overview">
          <h2 className="text-sm font-semibold text-muted-foreground mb-2">Active Stock Takes ({activeSessions.length})</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {activeSessions.map(s => {
              const st = STATUS_MAP[s.status];
              const Icon = st?.icon || Clock;
              const pct = Math.round((s.counted_products || 0) / Math.max(s.total_products || 1, 1) * 100);
              return (
                <Card key={s.session_id} className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => openSession(s.session_id)} data-testid={`active-session-card-${s.session_id}`}>
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex gap-1 flex-wrap">
                        {(s.filters?.categories || []).map(c => <Badge key={c} variant="secondary" className="text-[9px]">{c}</Badge>)}
                        {!(s.filters?.categories?.length) && <Badge variant="secondary" className="text-[9px]">All categories</Badge>}
                      </div>
                      <Badge variant="outline" className={`text-[9px] gap-1 shrink-0 ${st?.color || ''}`}><Icon className="h-3 w-3" />{st?.label}</Badge>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span>Counted: <strong>{s.counted_products}</strong>/{s.total_products}</span>
                      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-muted-foreground">{pct}%</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground">By {s.created_by} · {new Date(s.created_at).toLocaleString()}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
          ) : sessions.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No stock takes yet</p>
              <p className="text-xs mt-1">Click "New Stock Take" to begin.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Session</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Progress</TableHead>
                  <TableHead className="hidden md:table-cell">Filters</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions.map(s => {
                  const st = STATUS_MAP[s.status];
                  const Icon = st?.icon || Clock;
                  return (
                    <TableRow key={s.session_id} className="cursor-pointer hover:bg-muted/50" onClick={() => openSession(s.session_id)} data-testid={`session-row-${s.session_id}`}>
                      <TableCell className="font-mono text-xs">{s.session_id}</TableCell>
                      <TableCell><Badge variant="outline" className={`text-[9px] gap-1 ${st?.color || ''}`}><Icon className="h-3 w-3" />{st?.label}</Badge></TableCell>
                      <TableCell className="text-center text-sm">{s.counted_products}/{s.total_products}</TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="flex gap-1 flex-wrap">
                          {(s.filters?.categories || []).map(c => <Badge key={c} variant="secondary" className="text-[8px]">{c}</Badge>)}
                          {(s.filters?.bin_locations || []).map(b => <Badge key={b} variant="outline" className="text-[8px] gap-0.5"><MapPin className="h-2.5 w-2.5" />{b}</Badge>)}
                          {!(s.filters?.categories?.length || s.filters?.bin_locations?.length) && <span className="text-[10px] text-muted-foreground">All</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{s.created_by}</TableCell>
                      <TableCell className="text-sm">{new Date(s.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          {s.status === 'pending_approval' && isAdmin && (
                            <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1 text-green-600" onClick={() => setApproveDialog(s.session_id)}>
                              <ShieldCheck className="h-3 w-3" /> Approve
                            </Button>
                          )}
                          {(s.status === 'in_progress' || s.status === 'pending_approval') && (
                            <Button size="sm" variant="ghost" className="h-7 text-[10px] text-destructive" onClick={() => handleCancel(s.session_id)}><Ban className="h-3 w-3" /></Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          {pages > 1 && (
            <div className="flex items-center justify-between p-3 border-t">
              <p className="text-xs text-muted-foreground">Page {page} of {pages}</p>
              <div className="flex gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= pages} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Stock Take Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="create-stocktake-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-primary" /> New Stock Take</DialogTitle>
            <DialogDescription>Select categories and/or bin locations to include. Leave empty for all products.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {availableCategories.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Categories (leave empty for all)</Label>
                <div className="grid grid-cols-2 gap-1.5 max-h-32 overflow-y-auto">
                  {availableCategories.map(cat => (
                    <label key={cat} className={`flex items-center gap-2 p-1.5 rounded text-xs cursor-pointer ${selectedCategories.includes(cat) ? 'bg-primary/10' : 'hover:bg-muted/50'}`}>
                      <Checkbox checked={selectedCategories.includes(cat)} onCheckedChange={() => setSelectedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat])} />
                      {cat}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {availableBins.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Bin Locations</Label>
                <div className="grid grid-cols-2 gap-1.5 max-h-32 overflow-y-auto">
                  {availableBins.map(bin => (
                    <label key={bin} className={`flex items-center gap-2 p-1.5 rounded text-xs cursor-pointer ${selectedBins.includes(bin) ? 'bg-primary/10' : 'hover:bg-muted/50'}`}>
                      <Checkbox checked={selectedBins.includes(bin)} onCheckedChange={() => setSelectedBins(prev => prev.includes(bin) ? prev.filter(b => b !== bin) : [...prev, bin])} />
                      <MapPin className="h-3 w-3" />{bin}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs">Notes</Label>
              <Input value={createNotes} onChange={e => setCreateNotes(e.target.value)} placeholder="Optional notes..." data-testid="create-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createSession} disabled={creating} className="gap-1.5" data-testid="confirm-create-btn">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Start Stock Take
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
